import UserService from './user.service';

let upcomingMeetingsCache = null;
let upcomingMeetingsCacheAt = 0;
const INDICATOR_CACHE_TTL_MS = 5 * 60 * 1000;

export function isMeetingsRoute(route) {
  if (!route) return false;
  const name = route.name || '';
  const path = route.path || '';
  return name === 'Meetings' || path === '/meetings' || path.startsWith('/meetings');
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Parse a meeting's date + time into a Date (handles YYYY-MM-DD and DD-MM-YYYY / DD/MM/YYYY). */
function parseMeetingDateTime(m) {
  if (!m || !m.meeting_date) return null;
  const d = String(m.meeting_date).trim();
  let ymd = null;
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
    ymd = d.slice(0, 10);
  } else if (/^\d{2}[-/]\d{2}[-/]\d{4}/.test(d)) {
    const p = d.split(/[-/]/);
    ymd = `${p[2]}-${p[1]}-${p[0]}`;
  }
  if (!ymd) return null;
  let hm = '23:59'; // no time → treat as end of that day
  const t = m.meeting_time ? String(m.meeting_time).trim() : '';
  const tm = t.match(/^(\d{1,2}):(\d{2})/);
  if (tm) hm = `${tm[1].padStart(2, '0')}:${tm[2]}`;
  const dt = new Date(`${ymd}T${hm}:00`);
  return isNaN(dt.getTime()) ? null : dt;
}

/** True if the meeting is today or tomorrow (i.e. show from one day before, and on the day). */
export function isMeetingWithinOneDay(item) {
  const m = item && item.meeting ? item.meeting : item;
  const dt = parseMeetingDateTime(m);
  if (!dt) return false;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfTomorrow = new Date(startOfToday.getTime() + 2 * ONE_DAY_MS - 1);
  return dt.getTime() >= startOfToday.getTime() && dt.getTime() <= endOfTomorrow.getTime();
}

/** Seed the indicator from data fetched elsewhere (home bootstrap, Meetings page). */
export function setUpcomingMeetingsIndicator(value) {
  upcomingMeetingsCache = !!value;
  upcomingMeetingsCacheAt = Date.now();
}

function cacheIsFresh() {
  return upcomingMeetingsCache !== null
    && (Date.now() - upcomingMeetingsCacheAt) < INDICATOR_CACHE_TTL_MS;
}

/**
 * Flag true only if an upcoming meeting is within one day.
 * Serves a fresh cached value (seeded here or via setUpcomingMeetingsIndicator)
 * without a network call; pass { force: true } to bypass the cache.
 */
export async function fetchUpcomingMeetingsIndicator({ force = false } = {}) {
  if (!force && cacheIsFresh()) {
    return upcomingMeetingsCache;
  }
  const response = await UserService.authPostRequest('get-meetings', { type: 'Upcoming' });
  if (response?.data?.status !== 'success') {
    setUpcomingMeetingsIndicator(false);
    return false;
  }
  const list = (response.data.data && response.data.data.list) || [];
  setUpcomingMeetingsIndicator(Array.isArray(list) && list.some(isMeetingWithinOneDay));
  return upcomingMeetingsCache;
}

export function getCachedUpcomingMeetingsIndicator() {
  return upcomingMeetingsCache;
}

export function clearUpcomingMeetingsCache() {
  upcomingMeetingsCache = null;
  upcomingMeetingsCacheAt = 0;
}
